import { useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { NavbarItem, NavbarDropdownItem } from '@/components/Navbar/types';
import { useAuth } from '@/contexts';
import { WebSocketContext } from '@/contexts/websocket';
import { LINKS } from '@/constants';
import { useFeatureFlags } from './useFeatureFlags';
import { useClient } from './useClient';
import { useTeamUpsell } from './useTeamUpsell';
import { clientPermissionsFor } from './useClientPermissions';
import { AgoraPlanNameE } from '@/types/cyclone/models';
import { trackEvent } from '@/analytics';
import { isUpHiddenCatalogPath } from '@/utils/agora-up-market';
import { isProfessionalLikePlan } from '@/utils/plans';

const createMenuItem = (
  label: string,
  path: string,
  currentPath: string | undefined,
  icon?: any,
  link?: string,
  dropdownContent?: NavbarItem[],
  external?: boolean,
  badge?: string
): NavbarItem => ({
  label,
  onClick: () => trackEvent('menu_item_clicked', { name: label }),
  isSelected: dropdownContent ? !!currentPath?.startsWith(path || '/---') : currentPath === path,
  link: link ?? path,
  ...(icon && { icon }),
  ...(dropdownContent && { isDropdown: true, dropdownContent }),
  ...(external && { external: true }),
  ...(badge && { badge })
});

const createDropdownItem = (
  label: string,
  path: string,
  currentPath: string | undefined,
  length?: number,
  icon?: any,
  link?: string,
  badge?: string
): NavbarItem => ({
  label,
  onClick: () => trackEvent('menu_item_clicked', { name: label }),
  isSelected: currentPath === path,
  link: link ?? path,
  ...(icon && { icon }),
  length,
  ...(badge && { badge })
});

/** `/agenda` cubre `/agenda` y `/agenda/...` (por segmento: no `/agendas`). `/` solo a sí misma. */
const coversPath = (link: string, currentPath: string): boolean =>
  link === currentPath || (link !== '/' && currentPath.startsWith(`${link}/`));

/**
 * Marca como activa la hoja (ítem sin submenú o hijo de un submenú) cuya ruta es el
 * prefijo más largo de la actual, así una pantalla anidada (`/agenda/ingresar-reserva`,
 * `/ajustes/horarios`) deja marcada su sección. Con el prefijo más largo nunca quedan
 * dos destinos distintos activos a la vez (`/ajustes/medios-de-cobro` gana sobre
 * `/ajustes`). Los ítems con submenú conservan su propia regla.
 */
const markActiveLeaf = (groups: NavbarItem[][], currentPath: string | undefined): NavbarItem[][] => {
  if (!currentPath) return groups;
  const current = currentPath.length > 1 ? currentPath.replace(/\/+$/, '') : currentPath;
  const isLeafLink = (item: NavbarDropdownItem): boolean =>
    !!item.link && item.link.startsWith('/') && !(item as NavbarItem).external;
  let best = '';
  const consider = (item: NavbarDropdownItem) => {
    if (isLeafLink(item) && coversPath(item.link, current) && item.link.length > best.length)
      best = item.link;
  };
  groups
    .flat()
    .forEach((item) => (item.dropdownContent ? item.dropdownContent.forEach(consider) : consider(item)));
  if (!best) return groups;
  const mark = <T extends NavbarDropdownItem>(item: T): T =>
    isLeafLink(item) ? { ...item, isSelected: item.link === best } : item;
  return groups.map((items) =>
    items.map((item) =>
      item.dropdownContent
        ? {
            ...item,
            isSelected: item.dropdownContent.some((child) => child.link === best),
            dropdownContent: item.dropdownContent.map(mark)
          }
        : mark(item)
    )
  );
};

// Enlaces del pie: fijos (no dependen de la sesión ni de la ruta), así el pie los lee
// directo sin correr el hook entero.
export const footerNavItems: NavbarItem[] = [
  {
    label: 'Preguntas frecuentes',
    link: 'https://ayuda.agora.red/help/collections/3f20159a-4ab3-4ea4-9f0f-c274ba38407f-preguntas-frecuentes',
    onClick: () => trackEvent('footer_link_clicked', { name: 'FAQ' })
  },
  {
    label: 'Términos y condiciones',
    link: LINKS.TERMS,
    onClick: () => trackEvent('footer_link_clicked', { name: 'Terms & Conditions' })
  },
  {
    label: 'Política de privacidad',
    link: LINKS.PRIVACY,
    onClick: () => trackEvent('footer_link_clicked', { name: 'Privacy Policy' })
  }
];

type NavbarItemsInput = {
  path: string | undefined;
  session: ReturnType<typeof useAuth>['session'];
  logOut: () => void;
  featureFlags: ReturnType<typeof useFeatureFlags>;
  myCommunitiesCount: number;
  /** Conversaciones sin responder (beta Conversaciones): el contador del ítem. */
  conversationsUnanswered?: number;
  navigate: (to: string) => void;
  openTeamUpsell: () => void;
};

type NavbarItems = { mainItems: NavbarItem[]; dropdownItems: NavbarItem[]; footerItems: NavbarItem[] };

/** Arma el menú. Puro: lo memoiza `useNavbarItems` con sus entradas reales. */
export const buildNavbarItems = ({
  path,
  session,
  logOut,
  featureFlags,
  myCommunitiesCount,
  conversationsUnanswered = 0,
  navigate,
  openTeamUpsell
}: NavbarItemsInput): NavbarItems => {
  // Gate del Addon Pro (antes «Pro» y «Finanzas Pro»): sin Pro, la sección se ve con candado y lleva al
  // onboarding que lo explica y lo activa. Finanzas sigue leyendo FINANZAS_ACTIVE (el staff
  // entra además con su permiso); el resto de Pro lee PRO_ACTIVE.
  const lockedToProOnboarding = (label: string): Pick<NavbarDropdownItem, 'locked' | 'onClick'> => ({
    locked: true,
    onClick: () => {
      trackEvent('menu_item_clicked', { name: `${label} (bloqueado)` });
      navigate('/facturacion/comisiones-onboarding');
    }
  });
  const getProGate = (label: string): Partial<Pick<NavbarDropdownItem, 'locked' | 'onClick'>> =>
    featureFlags.PRO_ACTIVE ? {} : lockedToProOnboarding(label);
  const getFinanzasAddOnGate = (label: string): Partial<Pick<NavbarDropdownItem, 'locked' | 'onClick'>> =>
    featureFlags.FINANZAS_ACTIVE
      ? {}
      : {
          locked: true,
          onClick: () => {
            trackEvent('menu_item_clicked', { name: `${label} (bloqueado)` });
            navigate('/facturacion/comisiones-onboarding');
          }
        };

  const isEventPlan = session?.vendor?.plan_name === AgoraPlanNameE.EVENT;
  const planName = session?.vendor?.plan_name;
  const isProfessionalPlan =
    planName === AgoraPlanNameE.PROFESSIONAL ||
    planName === AgoraPlanNameE.AGORA_UP ||
    planName === AgoraPlanNameE.CAR;
  const isAcademyPlan = session?.vendor?.plan_name === AgoraPlanNameE.ACADEMY;

  const shouldShowOnlineCourses = session?.vendor?.online_courses_enabled ?? false;

  const length = {
    services: session?.count_active_services,
    events: session?.count_active_events,
    subscriptions: session?.count_active_subscriptions,
    digitalContent: session?.count_active_digital_content,
    onlineCourses: session?.count_active_online_courses,
    products: session?.count_active_products
  } as const;

  const getCatalogDropdownContent = (includeSubscriptions = false) => {
    const content = [
      createDropdownItem('Servicios', '/servicios', path, length.services, 'briefcase'),
      createDropdownItem(
        isEventPlan ? 'Eventos' : 'Capacitaciones',
        '/eventos',
        path,
        length.events,
        'calendar-days'
      ),
      ...(shouldShowOnlineCourses
        ? [createDropdownItem('Cursos Online', '/cursos-online', path, length.onlineCourses, 'video')]
        : []),
      ...(featureFlags.PRODUCTS
        ? [
            createDropdownItem(
              'Productos',
              '/productos',
              path,
              length.products,
              'box-open',
              undefined,
              featureFlags.CATALOG_PRODUCTS_ACTIVE ? undefined : 'Activar'
            )
          ]
        : []),
      ...(includeSubscriptions
        ? [createDropdownItem('Planes mensuales', '/planesmensuales', path, length.subscriptions, 'repeat')]
        : []),
      createDropdownItem('Descargables', '/descargables', path, length.digitalContent, 'download'),
      createDropdownItem('Señas y precios', '/servicios/precios-y-senas', path, undefined, 'wallet')
    ];

    return featureFlags.CAR
      ? content.filter((item) => !['/eventos', '/planesmensuales', '/descargables'].includes(item.link ?? ''))
      : content;
  };

  // Analítica v2: ítem propio con sus secciones, en el orden de JP. El equipo no ve Equipo
  // ni Canales; en su lugar, sus reseñas.
  const getAnalyticsItem = (): NavbarItem => {
    const personal = session?.is_staff && session.vendor?.vendor_permissions?.analytics_view !== true;
    const kids: NavbarDropdownItem[] = [
      // Mismo orden que OWNER_SECTIONS/STAFF_SECTIONS de AnalyticsV2.
      createDropdownItem('Resumen', '/analytics/resumen', path, undefined, 'chart-line'),
      createDropdownItem('Agenda', '/analytics/agenda', path, undefined, 'calendar'),
      createDropdownItem('Servicios', '/analytics/servicios', path, undefined, 'briefcase'),
      ...(featureFlags.CATALOG_PRODUCTS_ACTIVE
        ? [createDropdownItem('Productos', '/analytics/productos', path, undefined, 'box-open')]
        : []),
      ...(!personal && session?.is_venue
        ? [createDropdownItem('Equipo', '/analytics/equipo', path, undefined, 'people-group')]
        : []),
      // Marketing viene con Pro. Sin Pro, el dueño la ve con candado (lleva al onboarding);
      // al equipo no se le ofrece contratar algo que no es suyo, así que no la ve.
      ...(!personal && (featureFlags.PRO_ACTIVE || !session?.is_staff)
        ? [
            {
              ...createDropdownItem('Marketing', '/analytics/canales', path, undefined, 'bullhorn'),
              addOn: true,
              ...getProGate('Marketing')
            }
          ]
        : []),
      ...(personal ? [createDropdownItem('Tus reseñas', '/analytics/resenas', path, undefined, 'star')] : []),
      // Tu mes en historias (beta por negocio). Fuera de OWNER_SECTIONS: es su propia pantalla.
      ...(featureFlags.TU_MES
        ? [
            createDropdownItem(
              'Historias',
              '/analytics/historias',
              path,
              undefined,
              'mobile',
              undefined,
              'Nuevo'
            )
          ]
        : [])
    ];
    return createMenuItem('Analítica', '/analytics', path, 'chart-line', '/analytics/resumen', kids);
  };

  const getFinanzasBase = () => [
    createDropdownItem('Resumen', '/finanzas', path, undefined, 'chart-line'),
    createDropdownItem('Ventas', '/finanzas/ventas', path, undefined, 'list'),
    createDropdownItem('Ventas por cobrar', '/finanzas/por-cobrar', path, undefined, 'clock'),
    createDropdownItem('Ventas cobradas', '/finanzas/cobros', path, undefined, 'wallet')
  ];

  // Actividad con una entrada por tabla (JP, 19/10): reemplaza el selector de la pantalla.
  // Mismo orden que el selector viejo, así la primera es la tabla por defecto. Las que
  // dependen de lo que vende el negocio salen de su catálogo (la pantalla igual abre
  // cualquiera por URL). Con una sola tabla queda como enlace plano.
  const getActivityItem = (): NavbarItem => {
    const kids: NavbarDropdownItem[] = [
      ...(featureFlags.FINANZAS_ACTIVE
        ? []
        : [createDropdownItem('Transacciones', '/actividad/transacciones', path, undefined, 'list')]),
      ...(isProfessionalLikePlan(planName)
        ? [createDropdownItem('Reservas', '/actividad/reservas', path, undefined, 'calendar')]
        : []),
      ...(isEventPlan || (length.events ?? 0) > 0
        ? [
            createDropdownItem(
              isEventPlan ? 'Entradas' : 'Capacitaciones',
              '/actividad/tickets',
              path,
              undefined,
              'calendar-days'
            )
          ]
        : []),
      ...(shouldShowOnlineCourses
        ? [createDropdownItem('Cursos online', '/actividad/cursos', path, undefined, 'video')]
        : []),
      ...(featureFlags.CATALOG_PRODUCTS_ACTIVE || (featureFlags.PRODUCTS && (length.products ?? 0) > 0)
        ? [createDropdownItem('Productos', '/actividad/productos', path, undefined, 'box-open')]
        : [])
    ];
    return kids.length > 1
      ? createMenuItem('Actividad', '/actividad', path, 'list', kids[0].link, kids)
      : createMenuItem('Actividad', '/actividad', path, 'list');
  };

  // Fichas y preguntas: sólo el dueño las configura (al staff no se le muestran).
  const fichasInMenu = featureFlags.CLIENT_FORMS && !session?.is_staff;
  // Clientes se abre en sub-ítems: la base de siempre, los segmentos y las fichas, en ese orden.
  // Lo usan el dueño y el staff (al staff le llegan Base y Segmentos: las fichas no son suyas).
  const getClientsItem = () => {
    const kids = [
      createDropdownItem('Base de clientes', '/clientes', path, undefined, 'address-book'),
      ...(featureFlags.CLIENT_SEGMENTS
        ? [
            createDropdownItem(
              'Segmentos',
              '/clientes/segmentos',
              path,
              undefined,
              'users',
              undefined,
              'Nuevo'
            )
          ]
        : []),
      ...(fichasInMenu
        ? [
            featureFlags.CLIENT_FIELDS_V2
              ? createDropdownItem('Fichas y preguntas', '/datos-clientes', path, undefined, 'file-lines')
              : createDropdownItem('Fichas', '/fichas', path, undefined, 'file-lines')
          ]
        : [])
    ];
    // En el celular, tocar «Clientes» sólo despliega sus sub-ítems (`expandOnly`).
    return kids.length > 1
      ? {
          ...createMenuItem('Clientes', '/clientes', path, 'address-book', '/clientes', kids),
          expandOnly: true
        }
      : createMenuItem('Clientes', '/clientes', path, 'address-book');
  };

  const getBusinessItems = () => [
    getActivityItem(),
    ...(featureFlags.LANDING_ACTIVITY
      ? [createMenuItem('Actividad landing', '/actividad-landing', path, 'store')]
      : []),
    getClientsItem(),
    ...(featureFlags.ANALYTICS_V2 ? [] : [createMenuItem('Analítica', '/analytics', path, 'chart-line')])
  ];

  const mainItems: NavbarItem[] = [];

  // Beta Conversaciones: la bandeja (y sus pestañas) cuelga de /conversaciones. El contador
  // son las conversaciones sin responder; el lugar en el rail lo decide SECTION_ORDER.
  const getConversationsItem = (): NavbarItem => ({
    ...createMenuItem('Conversaciones', '/conversaciones', path, 'message'),
    isSelected: path === '/conversaciones' || !!path?.startsWith('/conversaciones/'),
    ...(conversationsUnanswered > 0 && { count: conversationsUnanswered })
  });

  if (!session?.is_staff) {
    // Principal — quick access to the dashboard, storefront preview, and calendar.
    mainItems.push(
      createMenuItem('Inicio', '/', path, 'house'),
      createMenuItem('Mi página', '/mi-pagina', path, 'mobile')
    );
    if (featureFlags.CONVERSATIONS) mainItems.push(getConversationsItem());

    // Professional plan specific items
    if (isProfessionalPlan || session?.is_venue) {
      mainItems.push(
        createMenuItem('Agenda', '/agenda', path, 'calendar', '/agenda', [
          createDropdownItem('Calendario', '/agenda', path, undefined, 'calendar'),
          createDropdownItem('Horarios de trabajo', '/agenda/horarios-de-trabajo', path, undefined, 'clock'),
          createDropdownItem(
            'Bloqueos y vacaciones',
            '/agenda/bloquear-horarios',
            path,
            undefined,
            'calendar-days'
          ),
          createDropdownItem(
            'Políticas de reserva',
            '/servicios/edicion-global/politicas',
            path,
            undefined,
            'file-lines'
          )
        ])
      );
      if (featureFlags.ANALYTICS_V2) mainItems.push(getAnalyticsItem());

      if (featureFlags.FINANZAS) {
        // Módulo Addon Pro: toda la plata en un container. Caja y Comisiones se
        // MUDAN acá adentro — contratado, sus entradas viejas del rail desaparecen
        // (las rutas viejas siguen vivas y redirigen). La entrada se muestra aunque
        // el add-on NO esté contratado: ahí las rutas llevan al onboarding que lo
        // explica y lo activa, igual que Catálogo de Productos.
        const finanzasKids: NavbarDropdownItem[] = [
          ...getFinanzasBase(),
          // A partir de acá son las secciones del ADD-ON: llevan `addOn` para que el rail
          // las marque y se distingan de las cuatro de arriba, que son historia del negocio.
          // Comisiones va primero (pedido de Isabella, 27/ago).
          // Comisiones necesita estructura de equipo (gate ESTRUCTURAL, no comercial): un
          // profesional independiente se cobra a sí mismo, no liquida a nadie. Igual se
          // MUESTRA, bloqueada, y abre el mismo modal que la entrada "Equipo" del rail —
          // esconderla dejaba al profesional sin saber que existe ni cómo habilitarla.
          {
            ...createDropdownItem(
              'Comisiones del equipo',
              '/finanzas/comisiones',
              path,
              undefined,
              'people-group'
            ),
            addOn: true,
            ...(featureFlags.REVENUE_SHARE
              ? getFinanzasAddOnGate('Comisiones del equipo')
              : {
                  locked: true,
                  onClick: () => {
                    trackEvent('menu_item_clicked', { name: 'Comisiones del equipo (bloqueado)' });
                    openTeamUpsell();
                  }
                })
          },
          {
            ...createDropdownItem('Gastos', '/finanzas/gastos', path, undefined, 'tags'),
            addOn: true,
            ...getFinanzasAddOnGate('Gastos')
          },
          {
            ...createDropdownItem('Resultado', '/finanzas/resultado', path, undefined, 'chart-bar'),
            addOn: true,
            ...getFinanzasAddOnGate('Resultado')
          },
          ...(featureFlags.CASH_REGISTER_AVAILABLE
            ? [
                {
                  ...createDropdownItem(
                    'Gestión de caja',
                    '/finanzas/caja',
                    path,
                    undefined,
                    'cash-register'
                  ),
                  addOn: true,
                  ...getFinanzasAddOnGate('Gestión de caja')
                }
              ]
            : [])
        ];
        mainItems.push(
          createMenuItem('Finanzas', '/finanzas', path, 'cash-register', '/finanzas', finanzasKids)
        );
      }

      mainItems.push(
        createMenuItem('Catálogo', '', path, 'shop', '/servicios', getCatalogDropdownContent(true))
      );

      mainItems.push(...getBusinessItems());

      if (session?.is_venue) {
        if (featureFlags.USE_RESOURCES_NAV) {
          // Flat link, no dropdown — relabelled as "Recursos" for venues that
          // don't need the Pagos/Integrantes split.
          mainItems.push(createMenuItem('Recursos', '/equipo', path, 'people-group'));
        } else {
          const equipoSubItems: NavbarDropdownItem[] = [
            createDropdownItem('Integrantes', '/equipo', path, undefined, 'people-group'),
            // Fichaje del equipo: sólo el dueño ve la asistencia (el staff marca desde su celular).
            // Viene con Pro: sin Pro se ve con candado y lleva al onboarding.
            ...(session.vendor?.venue?.vendor_owner_id === session.vendor?.id
              ? [
                  {
                    ...createDropdownItem(
                      'Asistencia',
                      '/equipo/asistencia',
                      path,
                      undefined,
                      'clock',
                      undefined,
                      featureFlags.TEAM_ATTENDANCE ? 'Nuevo' : undefined
                    ),
                    addOn: true,
                    ...(featureFlags.TEAM_ATTENDANCE ? {} : lockedToProOnboarding('Asistencia'))
                  }
                ]
              : [])
          ];

          mainItems.push(createMenuItem('Equipo', '/equipo', path, 'people-group', '', equipoSubItems));
        }
      } else {
        // Solo Professional plan: surface "Equipo" as a visible-but-locked entry.
        // Clicking opens the upsell modal (next step + pricing impact + CTA)
        // instead of navigating anywhere.
        mainItems.push({
          ...createMenuItem('Equipo', '', path, 'people-group'),
          link: '',
          locked: true,
          onClick: () => {
            trackEvent('menu_item_clicked', { name: 'Equipo (bloqueado)' });
            openTeamUpsell();
          }
        });
      }

      mainItems.push(createMenuItem('Medios de cobro', '/ajustes/medios-de-cobro', path, 'wallet'));

      if (featureFlags.MESSAGES_TEMPLATES) {
        mainItems.push(
          createMenuItem('Mensajes automáticos', '/ajustes/mensajes-automaticos', path, 'comments')
        );
      }
      // "Descuentos" agrupa Códigos de descuento + Precios dinámicos como
      // sub-items colapsables (pedido JP). Si el flag de precios dinámicos está
      // apagado, queda como un link plano a Códigos de descuento.
      const descuentosSubItems: NavbarDropdownItem[] = [
        createDropdownItem('Códigos de descuento', '/descuentos', path, undefined, 'tags')
      ];
      if (featureFlags.DYNAMIC_PRICING) {
        descuentosSubItems.push(
          createDropdownItem('Precios dinámicos', '/precios-dinamicos', path, undefined, 'bolt')
        );
      }
      // Ofertas es la tercera forma de bajar un precio, junto con los códigos y
      // los precios dinámicos: va DENTRO del mismo grupo, no como un destino
      // hermano. Suelta, el profesional tiene que adivinar en cuál de los dos
      // lugares configura un descuento.
      if (featureFlags.OFFERS) {
        descuentosSubItems.push(createDropdownItem('Ofertas', '/ofertas', path, undefined, 'bullhorn'));
      }
      mainItems.push(
        descuentosSubItems.length > 1
          ? createMenuItem('Descuentos', '', path, 'tags', '', descuentosSubItems)
          : createMenuItem('Descuentos', '/descuentos', path, 'tags')
      );
      mainItems.push(createMenuItem('Tarjetas de regalo', '/tarjetas-de-regalo', path, 'gift'));
      // Premios reemplaza a Fidelización para la cohorte de la beta: son el mismo
      // programa, así que nunca se muestran los dos.
      if (featureFlags.PREMIOS) {
        // Premios viene con Pro: lleva la estrellita pero no candado, para que su placeholder
        // explique qué es antes de pedir Pro (sin Pro, el botón es «Sumar Addon Pro»).
        // Trofeo y no estrella: con la estrellita de Pro al lado quedaba doble estrella.
        mainItems.push({ ...createMenuItem('Premios', '/premios', path, 'trophy'), addOn: true });
      } else if (featureFlags.LOYALTY) {
        mainItems.push(createMenuItem('Fidelización', '/fidelizacion', path, 'star'));
      }
      // Comunidad: el rail permite descubrir el add-on. Antes de activarlo abre su
      // onboarding; después abre el centro de trabajo.
      if (featureFlags.COMMUNITY) {
        // Comunidad es un asset: crear ES activar. El destino es siempre /comunidad —
        // sin comunidad creada, esa ruta redirige sola al wizard. El viejo destino
        // /facturacion/comunidad-onboarding murió con el add-on y era un 404.
        mainItems.push(createMenuItem('Comunidad', '/comunidad', path, 'comments'));
      }
      // Contenido IG (30/9/26): «Instructivo IG» y «Compartir horarios» eran dos entradas del
      // menú; ahora es una sola sección con un hub que ofrece los dos caminos.
      mainItems.push(createMenuItem('Contenido IG', '/contenido-ig', path, 'instagram'));
    }

    // Event plan specific items
    if (isEventPlan && !session?.is_staff) {
      if (featureFlags.ANALYTICS_V2) mainItems.push(getAnalyticsItem());
      mainItems.push(
        createMenuItem('Catálogo', '', path, 'shop', '/eventos', [
          createDropdownItem(
            isEventPlan ? 'Eventos' : 'Capacitaciones',
            '/eventos',
            path,
            length.events,
            'calendar-days'
          ),
          createDropdownItem('Descargables', '/descargables', path, length.digitalContent, 'download'),
          ...(shouldShowOnlineCourses
            ? [createDropdownItem('Cursos Online', '/cursos-online', path, length.onlineCourses, 'video')]
            : [])
        ]),
        createMenuItem('Promotores', '/promotores', path, 'bullhorn'),
        ...getBusinessItems(),
        createMenuItem('Medios de cobro', '/ajustes/medios-de-cobro', path, 'wallet'),
        createMenuItem('Descuentos', '/descuentos', path, 'tags')
      );
    }

    // Academy plan specific items: focused on online courses and events
    // No services, subscriptions, promoters, or staff management
    if (isAcademyPlan && !session?.is_staff) {
      if (featureFlags.ANALYTICS_V2) mainItems.push(getAnalyticsItem());
      mainItems.push(
        createMenuItem('Catálogo', '', path, 'shop', '/cursos-online', [
          createDropdownItem('Cursos Online', '/cursos-online', path, length.onlineCourses, 'video'),
          createDropdownItem('Capacitaciones', '/eventos', path, length.events, 'calendar-days'),
          createDropdownItem('Descargables', '/descargables', path, length.digitalContent, 'download')
        ]),
        ...getBusinessItems(),
        ...(featureFlags.MESSAGES_TEMPLATES
          ? [createMenuItem('Mensajes automáticos', '/ajustes/mensajes-automaticos', path, 'comments')]
          : []),
        createMenuItem('Medios de cobro', '/ajustes/medios-de-cobro', path, 'wallet'),
        createMenuItem('Descuentos', '/descuentos', path, 'tags')
      );
    }
  } else {
    // Staff specific items — Agenda, Actividad y Analítica son de base; Servicios,
    // Clientes y Finanzas los gatean permisos configurables.
    const staffPerms = session?.vendor?.vendor_permissions;
    if (staffPerms?.services_view) {
      mainItems.push(createMenuItem('Servicios', '/servicios', path, 'briefcase'));
    }
    mainItems.push(
      createMenuItem('Agenda', '/agenda', path, 'calendar'),
      createMenuItem('Actividad', '/actividad', path, 'list')
    );
    // `CONVERSATIONS` ya exige `conversations_view` para el equipo.
    if (featureFlags.CONVERSATIONS) mainItems.push(getConversationsItem());
    // Solo «Puede ver la ficha de sus clientes»: el contacto (`clients_view_all`) ya no abre Clientes.
    if (clientPermissionsFor(session).canViewClients) {
      mainItems.push(getClientsItem());
    }
    // Analítica va SIEMPRE: `analytics_view` dejó de decidir si el profesional entra y pasó
    // a decidir su ALCANCE — prendido ve el negocio entero (todas las sucursales), apagado
    // ve solo sus propios números. Definición de JP, 1/sep/2026.
    mainItems.push(
      featureFlags.ANALYTICS_V2
        ? getAnalyticsItem()
        : createMenuItem('Analítica', '/analytics', path, 'chart-line')
    );
    // El flag ya exige agenda_view + agenda_edit (legado: la API lo deriva de cualquier escritura
    // en la agenda) para el equipo: quien abría el
    // estudio desde la barra de la Agenda lo sigue encontrando, ahora en el menú.
    if (featureFlags.AVAILABILITY_SHARE) {
      mainItems.push(createMenuItem('Contenido IG', '/contenido-ig', path, 'instagram'));
    }
    // Finanzas en solo lectura. Se muestra únicamente si el permiso está prendido Y el
    // NEGOCIO tiene el entitlement: a diferencia del dueño, al equipo no se le ofrece
    // descubrir el add-on — no es suyo para contratar, así que una entrada bloqueada que
    // lleva al onboarding de facturación sería un callejón sin salida.
    if (staffPerms?.finanzas_view && session?.business_finanzas_entitlement) {
      mainItems.push(
        createMenuItem('Finanzas', '/finanzas', path, 'cash-register', '/finanzas', [
          ...getFinanzasBase(),
          ...(featureFlags.REVENUE_SHARE
            ? [
                createDropdownItem(
                  'Comisiones del equipo',
                  '/finanzas/comisiones',
                  path,
                  undefined,
                  'people-group'
                )
              ]
            : []),
          createDropdownItem('Gastos', '/finanzas/gastos', path, undefined, 'tags'),
          createDropdownItem('Resultado', '/finanzas/resultado', path, undefined, 'chart-bar')
        ])
      );
    }
  }

  // GA para cualquier negocio/plan. El inbox privado y la configuración son del dueño.
  if (featureFlags.REVIEWS && !session?.is_staff) {
    mainItems.push(createMenuItem('Reseñas', '/resenas', path, 'star-half'));
  }

  // "Mis cursos" — lo que el profesional COMPRÓ o le dio su plan, no lo que
  // vende (eso es "Cursos online", dentro de Catálogo). Va afuera del if de
  // staff a propósito: un profesional del equipo también compra cursos.
  // El contador viene en la sesión, así que no cuesta un request extra: si no
  // tiene nada, el ítem no existe.
  if ((session?.count_my_course_enrollments ?? 0) > 0) {
    mainItems.push(createMenuItem('Mis cursos', '/mis-cursos', path, 'graduation-cap'));
  }

  // "Mis comunidades" — espacios de OTROS profesionales donde participa como
  // miembro (el acceso se resuelve por email en cyclone). No hay contador en la
  // sesión: se pregunta una vez por sesión de panel y se cachea; si no participa
  // en ninguna ajena, el ítem no existe. Al equipo no: /mis-comunidades no es una de sus rutas
  // (staffRoutes) y el ítem lo devolvía a la Agenda.
  if (myCommunitiesCount > 0 && !session?.is_staff) {
    mainItems.push(createMenuItem('Mis comunidades', '/mis-comunidades', path, 'users'));
  }

  // Ideas — entry point del feedback loop (beta). Aparece antes del Centro de
  // ayuda porque representa comunicación del vendor hacia Ágora (mientras que
  // el Centro de ayuda es comunicación al revés).
  const systemNavItems: NavbarItem[] = [];
  if (featureFlags.IDEA_BOX && !session?.is_staff) {
    systemNavItems.push(createMenuItem('Tus ideas', '/ideas', path, 'lightbulb'));
  }
  systemNavItems.push(
    createMenuItem(
      'Centro de ayuda',
      '',
      path,
      'circle-question',
      'https://ayuda.agora.red/help',
      undefined,
      true
    )
  );
  mainItems.push(...systemNavItems);

  const isInitialSetup =
    !session?.is_staff &&
    !isAcademyPlan &&
    !session?.vendor?.vendor_activity_tracking?.first_payment_method_at &&
    !session?.vendor?.vendor_activity_tracking?.first_availability_at &&
    !session?.vendor?.vendor_activity_tracking?.first_service_at;

  const dropdownItems: NavbarItem[] = isInitialSetup
    ? [createMenuItem('Medios de cobro', '/ajustes/medios-de-cobro', path, 'wallet')]
    : session?.is_staff
      ? []
      : [
          ...(isProfessionalPlan ? [createMenuItem('Mi plan', '/facturacion', path, 'credit-card')] : []),
          createMenuItem('Ajustes generales', '/ajustes', path, 'gear'),
          createMenuItem(
            isProfessionalPlan ? 'Referidos' : 'Invitaciones',
            '/referidos',
            path,
            'people-group'
          )
        ];
  dropdownItems.push({ ...createMenuItem('Salir', '', path, 'sign-out'), onClick: logOut });

  const visibleItems = (items: NavbarItem[]): NavbarItem[] =>
    items
      .filter((item) => !isUpHiddenCatalogPath(item.link))
      .map((item) => ({
        ...item,
        ...(item.dropdownContent ? { dropdownContent: visibleItems(item.dropdownContent) } : {})
      }));

  const activePath =
    // Con las fichas en el menú se marca su propio sub-ítem; si no, la sección Clientes.
    featureFlags.CLIENT_FORMS &&
    !fichasInMenu &&
    (path?.startsWith('/fichas') || path?.startsWith('/datos-clientes'))
      ? '/clientes'
      : path === '/seguimiento'
        ? '/ajustes/integraciones'
        : path;
  const [shownMainItems, shownDropdownItems] = markActiveLeaf(
    featureFlags.UP_INTERNATIONAL
      ? [visibleItems(mainItems), visibleItems(dropdownItems)]
      : [mainItems, dropdownItems],
    activePath
  );

  return {
    mainItems: shownMainItems,
    dropdownItems: shownDropdownItems,
    footerItems: footerNavItems
  };
};

export const useNavbarItems = (path?: string): NavbarItems => {
  const { logOut, session } = useAuth();
  const featureFlags = useFeatureFlags();
  const { client } = useClient();
  const { openTeamUpsell } = useTeamUpsell();
  const navigate = useNavigate();
  // `navigate` y `openTeamUpsell` cambian de identidad en cada cambio de ruta y solo se usan
  // al tocar un ítem: van por ref para que no rearmen el menú (la ruta ya lo rearma).
  const handlersRef = useRef({ navigate, openTeamUpsell });
  useLayoutEffect(() => {
    handlersRef.current = { navigate, openTeamUpsell };
  });

  // Comunidades AJENAS donde participa como miembro (la propia no cuenta: su
  // casa es /comunidad). Una vez por sesión de panel; si es 0, el ítem no existe.
  const { data: myCommunitiesData } = useQuery({
    queryKey: ['my_communities'],
    queryFn: async () =>
      client<{ communities: Array<{ vendor: { username: string } }> }>('me/communities', 'GET', {
        isAuthRequired: true
      }),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    enabled: !!session?.vendor && !session?.is_staff
  });
  const myCommunitiesCount = (myCommunitiesData?.communities ?? []).filter(
    (c) => c.vendor.username !== session?.vendor?.username
  ).length;

  // Beta Conversaciones: sin responder, para el contador del ítem. Los eventos del socket
  // invalidan esta clave; mientras tanto se refresca cada minuto.
  const { data: conversationsSummary } = useQuery({
    queryKey: ['conversations', 'summary'],
    queryFn: async () => {
      // Sin la API desplegada: la bandeja de ejemplo (Estudio Brisa) de `fixtures.ts`.
      if (import.meta.env.VITE_CONVERSATIONS_FIXTURES === 'true') {
        const { fixtureApi } = await import('@/containers/Conversaciones/fixtures');
        return fixtureApi.summary();
      }
      return client<{ unanswered: number; whatsapp: unknown }>('me/vendor/conversations/summary', 'GET', {
        isAuthRequired: true,
        suppressNotFoundToast: true,
        suppressNetworkToast: true
      });
    },
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    retry: false,
    enabled: featureFlags.CONVERSATIONS
  });
  const conversationsUnanswered = conversationsSummary?.unanswered ?? 0;
  // En vivo: cualquier evento de la bandeja cambia el contador. Sin proveedor de socket
  // (stories, pruebas) queda el refresco de cada minuto.
  const wsSubscribe = useContext(WebSocketContext)?.subscribe;
  const queryClient = useQueryClient();
  const conversationsOn = featureFlags.CONVERSATIONS;
  useEffect(() => {
    if (!wsSubscribe || !conversationsOn) return undefined;
    const refresh = () => queryClient.invalidateQueries({ queryKey: ['conversations', 'summary'] });
    const offs = [
      'conversation:message',
      'conversation:updated',
      'conversation:channel',
      'conversation:deleted'
    ].map((e) => wsSubscribe(e, refresh));
    return () => offs.forEach((off) => off());
  }, [wsSubscribe, conversationsOn, queryClient]);

  // Ruta, sesión (plan, permisos, contadores), flags y comunidades: lo único que cambia el menú.
  return useMemo(
    () =>
      buildNavbarItems({
        path,
        session,
        logOut,
        featureFlags,
        myCommunitiesCount,
        conversationsUnanswered,
        navigate: (to) => handlersRef.current.navigate(to),
        openTeamUpsell: () => handlersRef.current.openTeamUpsell()
      }),
    [path, session, logOut, featureFlags, myCommunitiesCount, conversationsUnanswered]
  );
};
